import React, { useState } from 'react';
import { CheckBadgeIcon, ClipboardIcon } from '@heroicons/react/24/outline';

interface StripeIndicatorProps {
  stripeProductId?: string;
  stripePriceId?: string;
  className?: string;
}

export const StripeIndicator: React.FC<StripeIndicatorProps> = ({
  stripeProductId,
  stripePriceId,
  className = '',
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);

  const hasStripeIntegration = !!(stripeProductId || stripePriceId);

  if (!hasStripeIntegration) {
    return null;
  }

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(type);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleMouseEnter = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowTooltip(false);
    }, 100); // Small delay to allow mouse to move to tooltip
    setHideTimeout(timeout);
  };

  return (
    <div
      className={`relative inline-block ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Stripe Badge */}
      <div className="inline-flex items-center">
        <CheckBadgeIcon className="h-4 w-4 text-green-500" />
        <span className="ml-1 text-xs font-medium text-green-600">Stripe</span>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-1">
          {/* Invisible bridge to connect tooltip to badge */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-full h-2" />

          <div
            className="p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="space-y-2">
              {stripeProductId && (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-300">Product ID:</span>
                  <code className="bg-gray-800 px-2 py-1 rounded text-green-300 font-mono text-xs">
                    {stripeProductId}
                  </code>
                  <button
                    onClick={() => copyToClipboard(stripeProductId, 'product')}
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    title="Copy Product ID"
                  >
                    <ClipboardIcon
                      className={`h-3 w-3 ${copiedId === 'product' ? 'text-green-400' : 'text-gray-400'}`}
                    />
                  </button>
                  {copiedId === 'product' && <span className="text-green-400 text-xs">✓</span>}
                </div>
              )}

              {stripePriceId && (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-300">Price ID:</span>
                  <code className="bg-gray-800 px-2 py-1 rounded text-blue-300 font-mono text-xs">
                    {stripePriceId}
                  </code>
                  <button
                    onClick={() => copyToClipboard(stripePriceId, 'price')}
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    title="Copy Price ID"
                  >
                    <ClipboardIcon
                      className={`h-3 w-3 ${copiedId === 'price' ? 'text-green-400' : 'text-gray-400'}`}
                    />
                  </button>
                  {copiedId === 'price' && <span className="text-green-400 text-xs">✓</span>}
                </div>
              )}
            </div>

            {/* Tooltip arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};
